import React, { useState, useEffect } from 'react';
import InvoiceTemplate from '../../components/Invoice/InvoiceTemplate';
import Button from '../../components/Button';
import { useSearchParams } from 'react-router-dom';
import { usePDF } from 'react-to-pdf';
import { calculatePriceWithMarkup, getMarkupFromURL } from '../../utils/markupCalculation';
import getCurrency from '../../utils/getCurrency';
import { config } from '../../constants';
import { calculateInvoiceTax } from '../../utils/salesTaxCalculation';

const InvoicePage = () => {
  const [searchParams] = useSearchParams();
  const { toPDF, targetRef } = usePDF({filename: 'Invoice.pdf'});
  
  const markupValue = getMarkupFromURL();
  
  const [invoiceData, setInvoiceData] = useState({
    invoiceNumber: "ZET1001",
    salesTax: "",
    salesTaxAmount: "",
    totalWithTax: "",
  });
  const [buyerTotal, setBuyerTotal] = useState(0);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [isLoadingTax, setIsLoadingTax] = useState(false);
  const [location, setLocation] = useState(null);
  const [designAmount, setDesignAmount] = useState(0);
  const [shippingAmount, setShippingAmount] = useState(0);
  const [designCredit, setDesignCredit] = useState(0);

  const handlePDFGeneration = async () => {
    setIsGeneratingPDF(true);
    try {
      await toPDF();
    } catch (error) {
      console.error('Error generating PDF:', error);
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  useEffect(() => {
    const dataParam = searchParams.get('data');
    if (dataParam) {
      try {
        const parsedData = JSON.parse(decodeURIComponent(dataParam));
        setLocation({
          city: parsedData.propertyDetails?.city,
          state: parsedData.propertyDetails?.state,
          zip: parsedData.propertyDetails?.zip,
        });
        
        // Start loading for tax calculation if we have location data
        if (parsedData.propertyDetails?.city || parsedData.propertyDetails?.zip) {
          setIsLoadingTax(true);
        }

        const baseAmountValue = parsedData.buyerTotal || 0;
        const finalAmount = parseFloat(calculatePriceWithMarkup(baseAmountValue, markupValue));
        // Calculate 15% of the final amount
        const ShippingAndInstallationAmount = finalAmount * 0.15;
        
        setDesignAmount(finalAmount);
        setShippingAmount(ShippingAndInstallationAmount);
        setBuyerTotal(finalAmount);
        setDesignCredit(parseFloat(parsedData.propertyDetails.design_credit) || 0);
        
        const invoiceNumber = `RV3D-${parsedData.id || '0001'}`;
        setInvoiceData(prev => ({
          ...prev,
          invoiceNumber,
          amountDue: getCurrency(finalAmount),
          shippingAmount: getCurrency(ShippingAndInstallationAmount)
        }));
      } catch (error) {
        console.error('Error parsing invoice data:', error);
      }
    }
  }, [searchParams, markupValue]);
  
  useEffect(() => {
    const fetchSalesTax = async () => {
      let query = '';
      if (location?.zip) {
        query = `zip_code=${encodeURIComponent(location.zip)}`;
      } else if (location?.city && location?.state) {
        query = `city=${encodeURIComponent(location.city)}&state=${encodeURIComponent(location.state)}`;
      } else {
        setIsLoadingTax(false);
        return;
      }

      setIsLoadingTax(true);
      try {
        const response = await fetch(`${config.TAX_API.BASE_URL}/salestax?${query}`, {
          headers: {
            'X-Api-Key': config.TAX_API.KEY,
          },
        });
        if (!response.ok) {
          throw new Error('Failed to fetch sales tax');
        }
        const data = await response.json();
        
        const taxRateDecimal = parseFloat(data[0]?.state_rate) || 0;
        const taxRatePercentage = taxRateDecimal * 100; // Convert to percentage for display
        
        // Calculate the total base amount 
        const totalBaseAmount = designAmount;
        
        // Calculate tax on the total base amount
        const taxCalculation = calculateInvoiceTax(totalBaseAmount, taxRateDecimal);
        
        // Calculate the total amount including tax
        const totalWithTax = totalBaseAmount + taxCalculation.taxAmount;
        
        // Apply design credit to the total amount (base + tax)
        const finalAmountDue = Math.max(0, totalWithTax - designCredit);
        
        setInvoiceData(prev => ({
          ...prev,
          salesTax: `${taxRatePercentage.toFixed(2)}%`,
          salesTaxAmount: getCurrency(taxCalculation.taxAmount),
          totalWithTax: getCurrency(finalAmountDue)
        }));
      } catch (error) {
        console.error('Error fetching sales tax:', error);
        // Calculate without tax for fallback
        const totalBaseAmount = designAmount;
        const finalAmountDue = Math.max(0, totalBaseAmount - designCredit);
        
        // Set default values if API fails (no tax)
        setInvoiceData(prev => ({
          ...prev,
          salesTax: "0%",
          salesTaxAmount: getCurrency(0),
          totalWithTax: getCurrency(finalAmountDue)
        }));
      } finally {
        setIsLoadingTax(false);
      }
    };

    fetchSalesTax();
  }, [location, designAmount, shippingAmount, designCredit]);

  return (
    <div className="min-h-screen bg-gray-50 py-8 relative">
      {/* Loading Overlay */}
      {isLoadingTax && (
        <div className="fixed inset-0 bg-white bg-opacity-95 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="text-center bg-white p-8 rounded-lg shadow-lg">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
            <div className="text-lg font-medium text-gray-900 mb-2">Calculating Invoice</div>
            <div className="text-sm text-gray-600">Fetching tax rates and calculating totals...</div>
          </div>
        </div>
      )}
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header with actions */}
        <div className="mb-6 flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Invoice</h1>
            <p className="text-gray-600 mt-1">Invoice #{invoiceData.invoiceNumber}</p>
          </div>
          <div className="flex gap-3">
            <Button
              onClick={handlePDFGeneration}
              disabled={isGeneratingPDF}
              className="flex items-center gap-2"
            >
              {isGeneratingPDF ? (
                <>
                  <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Generating PDF...
                </>
              ) : (
                <>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                  </svg>
                  Download PDF
                </>
              )}
            </Button>
          </div>
        </div>

        {/* Invoice Template */}
        <div className="bg-white rounded-lg shadow-lg overflow-hidden" ref={targetRef}>
          <InvoiceTemplate
            invoiceNumber={invoiceData.invoiceNumber}
            // paymentDue={invoiceData.paymentDue}
            amountDue={invoiceData.totalWithTax || invoiceData.amountDue}
            designAmount={invoiceData.amountDue}
            shippingAmount={invoiceData.shippingAmount}
            salesTax={invoiceData.salesTax}
            salesTaxAmount={invoiceData.salesTaxAmount}
            totalWithTax={invoiceData.totalWithTax}
            designCredit={designCredit}
            buyerTotal={buyerTotal}
          />
        </div>
      </div>

      {/* Print styles */}
      <style jsx>{`
        @media print {
          .no-print {
            display: none !important;
          }
          
          body {
            margin: 0;
            padding: 0;
          }
          
          .min-h-screen {
            min-height: auto;
          }
          
          .bg-gray-50 {
            background: white;
          }
          
          .shadow-lg {
            box-shadow: none;
          }
          
          .rounded-lg {
            border-radius: 0;
          }
        }
      `}</style>
    </div>
  );
};

export default InvoicePage;
